import React from 'react';

import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';

import {
    Box,
    Paper,
    Skeleton,
    Stack,
    Typography,
} from '@thirdspacelearning/library';
import { QUERY_STATUS } from '@tsl-frontend/tsl-utilities';

import Badge from './Badge';

import { IBadge } from '../../types';

import { currentProgrammeSelector } from '@store/programme';

import { getBadges } from '@api';
import { StudentQueryKeys } from '@services';
import { badgeBgSvg } from '@assets/dashboard';

const sx = {
    root: {
        position: 'relative',
        overflow: 'hidden',
        minHeight: '130px',
    },
    badgesInfo: {
        flexDirection: { sm: 'column', md: 'row' },
        alignItems: { sm: 'flex-start', md: 'center' },
        justifyContent: 'center',
        flexGrow: 1,
        gap: 2,
        pl: 4,
        pr: 0,
        py: 3,
    },
    icon: {
        fontSize: '4.5rem',
        border: '6px solid',
        borderRadius: '50%',
        boxSizing: 'content-box',
        borderColor: 'grey.300',
    },
    badgesBg: {
        position: 'absolute',
        bottom: { xs: 0, md: -50 },
        right: 26,
        zIndex: 0,
    },
    iconSeparator: {
        width: '70px',
        height: '10px',
        backgroundColor: 'grey.100',
        borderRadius: '12px',
        display: {
            xs: 'none',
            md: 'block',
        },
    },
};

const batchDummy = Array.from(new Array(3));

const BadgesPanel = () => {
    const currentProgramme = useSelector(currentProgrammeSelector);

    const { status, data: badgeList } = useQuery<IBadge[]>({
        queryKey: [StudentQueryKeys.BADGES],
        queryFn: () => getBadges(currentProgramme?.id),
        retry: 1,
        refetchOnWindowFocus: false,
        enabled: !!currentProgramme,
    });

    if (!currentProgramme) {
        return;
    }

    return (
        <Paper variant="outlined" sx={sx.root}>
            <Stack direction="row" height="100%">
                <Stack sx={sx.badgesInfo}>
                    <Stack>
                        {status === QUERY_STATUS.pending && (
                            <Skeleton variant="text" width="180px" />
                        )}
                        {status === QUERY_STATUS.success && (
                            <Typography
                                variant="h5"
                                fontWeight="bold"
                                data-testid="programmes-label"
                            >
                                Your Programme Badges
                            </Typography>
                        )}
                    </Stack>
                    <Stack
                        direction="row"
                        gap={2}
                        zIndex={1}
                        justifyContent="center"
                        flexGrow={1}
                        flexWrap="wrap"
                        data-testid="badges-panel"
                        flexShrink={0}
                        sx={{
                            justifyContent: {
                                sm: 'flex-start',
                                md: 'center',
                            },
                            alignItems: 'center',
                        }}
                    >
                        {status === QUERY_STATUS.pending &&
                            batchDummy.map((_, idx) => (
                                <React.Fragment key={idx}>
                                    <Skeleton
                                        variant="circular"
                                        width="84px"
                                        height="84px"
                                    />

                                    {idx !== batchDummy.length - 1 && (
                                        <Skeleton
                                            variant="rectangular"
                                            width="60px"
                                            height="10px"
                                        />
                                    )}
                                </React.Fragment>
                            ))}

                        {badgeList &&
                            badgeList.map((badge, idx) => (
                                <React.Fragment key={idx}>
                                    <Badge key={idx} badge={badge} />

                                    {idx !== badgeList.length - 1 && (
                                        <Box sx={sx.iconSeparator} />
                                    )}
                                </React.Fragment>
                            ))}
                    </Stack>
                </Stack>

                <Stack
                    sx={{
                        width: '160px',
                        height: '120px',
                        position: 'relative',
                        flexShrink: 0,
                        alignSelf: 'flex-end',
                    }}
                >
                    <Box
                        component="img"
                        alt="TSL character presenting badges"
                        src={badgeBgSvg}
                        sx={sx.badgesBg}
                    />
                </Stack>
            </Stack>
        </Paper>
    );
};

export default BadgesPanel;
